import { useMemo, useState } from 'react';
import {
  DndContext, DragOverlay, type DragStartEvent, type DragOverEvent, type DragEndEvent,
  KeyboardSensor, PointerSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { Decision, Item, Lifecycle } from '@mas-flow/types';
import { KanbanColumn } from './KanbanColumn';
import { ItemCardOverlay } from './ItemCard';
import { useRankItem } from '../hooks/useItems';
import { useToast } from '../hooks/useToast';
import type { GroupBy } from '../lib/filters/types';
import { useCategories } from '../hooks/useCategories';

const DECISION_COLUMNS: { id: string; title: string; decision: Decision | null }[] = [
  { id: 'inbox',  title: 'Inbox',  decision: null },
  { id: 'now',    title: 'Now',    decision: 'Now' },
  { id: 'next',   title: 'Next',   decision: 'Next' },
  { id: 'later',  title: 'Later',  decision: 'Later' },
  { id: 'parked', title: 'Parked', decision: 'Parked' },
];

const LIFECYCLE_COLUMNS: { id: string; title: string; lifecycle: Lifecycle }[] = [
  { id: 'submitted', title: 'Submitted', lifecycle: 'Submitted' },
  { id: 'intriage',  title: 'In Triage', lifecycle: 'InTriage' },
  { id: 'scored',    title: 'Scored',    lifecycle: 'Scored' },
  { id: 'decided',   title: 'Decided',   lifecycle: 'Decided' },
  { id: 'done',      title: 'Done',      lifecycle: 'Done' },
];

const PRIORITY_BAND_COLUMNS: { id: string; title: string; band: string }[] = [
  { id: 'pb-high', title: 'High',     band: 'High' },
  { id: 'pb-med',  title: 'Medium',   band: 'Medium' },
  { id: 'pb-low',  title: 'Low',      band: 'Low' },
  { id: 'pb-uns',  title: 'Unscored', band: 'Unscored' },
];

interface KanbanBoardProps {
  items: Item[];
  groupBy: GroupBy;
}

export function KanbanBoard({ items, groupBy }: KanbanBoardProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const rank = useRankItem();
  const toast = useToast();
  const { data: categories } = useCategories();
  const categoryColumns = (categories ?? []).map((c) => ({ id: `cat-col-${c.id}`, title: c.name, categoryId: c.id }));

  const columns = useMemo(() => {
    const active = items.filter((it) => it.lifecycle !== 'Archived');
    if (groupBy === 'priorityBand') {
      return PRIORITY_BAND_COLUMNS.map((col) => ({
        id: col.id,
        title: col.title,
        items: active
          .filter((it) => (it.priorityBandOverride ?? it.priorityBand ?? 'Unscored') === col.band)
          .sort((a, b) => a.columnRank.localeCompare(b.columnRank)),
      }));
    }
    if (groupBy === 'category') {
      return categoryColumns.map((col) => ({
        id: col.id,
        title: col.title,
        items: active
          .filter((it) => it.categoryId === col.categoryId)
          .sort((a, b) => a.columnRank.localeCompare(b.columnRank)),
      }));
    }
    if (groupBy === 'lifecycle') {
      return LIFECYCLE_COLUMNS.map((col) => ({
        id: col.id,
        title: col.title,
        items: active
          .filter((it) => it.lifecycle === col.lifecycle)
          .sort((a, b) => a.columnRank.localeCompare(b.columnRank)),
      }));
    }
    return DECISION_COLUMNS.map((col) => ({
      id: col.id,
      title: col.title,
      items: active
        .filter((it) => (col.decision === null ? it.decision === null : it.decision === col.decision))
        .sort((a, b) => a.columnRank.localeCompare(b.columnRank)),
    }));
  }, [items, groupBy, categoryColumns]);

  // Live drag state: which card is held, and the column/insertion-slot it is
  // currently hovering. Reassigning the card's column here (displayColumns)
  // is what lets the target bucket "make room" and gives the DragOverlay a
  // real slot to animate into on drop.
  //
  // `dropped` keeps the override alive after mouse-up: React flushes the
  // pointerup render (dnd-kit active=null) before the optimistic cache write
  // lands, so clearing the override immediately would put the card back in
  // its source column for exactly the frame the drop animation measures —
  // the overlay would fly back to the origin, then the card would teleport.
  const [drag, setDrag] = useState<{ id: string; startColumnId: string; columnId: string; index: number; dropped?: boolean } | null>(null);

  // Ordering matters: the pointerup flush renders first (ghost still in the
  // target slot — what the DragOverlay measures), then the optimistic cache
  // write re-renders the real card there (microtask), and only then does this
  // macrotask clear the now-redundant override.
  function commitDrop(d: NonNullable<typeof drag>) {
    setDrag({ ...d, dropped: true });
    setTimeout(() => setDrag((cur) => (cur?.dropped ? null : cur)), 0);
  }

  const displayColumns = useMemo(() => {
    if (!drag) return columns;
    const active = columns.flatMap((c) => c.items).find((it) => it.id === drag.id);
    if (!active) return columns;
    return columns.map((c) => {
      const without = c.items.filter((it) => it.id !== drag.id);
      if (c.id !== drag.columnId) return { ...c, items: without };
      const idx = Math.max(0, Math.min(drag.index, without.length));
      return { ...c, items: [...without.slice(0, idx), active, ...without.slice(idx)] };
    });
  }, [columns, drag]);

  const activeItem = drag ? items.find((it) => it.id === drag.id) ?? null : null;

  function onDragStart(e: DragStartEvent) {
    const id = String(e.active.id);
    for (const c of columns) {
      const index = c.items.findIndex((it) => it.id === id);
      if (index !== -1) {
        setDrag({ id, startColumnId: c.id, columnId: c.id, index });
        return;
      }
    }
  }

  function overColumnId(over: NonNullable<DragOverEvent['over']>): string | null {
    const overId = String(over.id);
    if (displayColumns.some((c) => c.id === overId)) return overId;
    const sortable = over.data.current?.sortable as { containerId?: string | number } | undefined;
    return sortable?.containerId != null ? String(sortable.containerId) : null;
  }

  function onDragOver(e: DragOverEvent) {
    if (!drag || !e.over) return;
    // Only decision columns accept cross-column drops; elsewhere the sortable
    // strategy previews same-column reordering on its own.
    if (groupBy !== 'decision') return;
    const overId = String(e.over.id);
    if (overId === drag.id) return;
    const colId = overColumnId(e.over);
    if (!colId || colId === drag.columnId) return;
    const targetLen =
      displayColumns.find((c) => c.id === colId)?.items.filter((it) => it.id !== drag.id).length ?? 0;
    const sortable = e.over.data.current?.sortable as { index?: number } | undefined;
    setDrag({ ...drag, columnId: colId, index: sortable?.index ?? targetLen });
  }

  function onDragEnd(e: DragEndEvent) {
    const d = drag;
    if (!d) return;
    const dragged = items.find((it) => it.id === d.id);
    if (!dragged) {
      setDrag(null);
      return;
    }

    if (groupBy === 'priorityBand' || groupBy === 'category') {
      // Within-column rank only; band/category aren't drag-rankable. Cross-column drag no-ops.
      setDrag(null);
      return;
    }

    // Final insertion slot within the target column, excluding the dragged card.
    const targetCol = displayColumns.find((c) => c.id === d.columnId);
    if (!targetCol) {
      setDrag(null);
      return;
    }
    const without = targetCol.items.filter((it) => it.id !== d.id);
    let insertAt = Math.max(0, Math.min(d.index, without.length));
    if (e.over && String(e.over.id) !== d.id) {
      const isColumn = displayColumns.some((c) => c.id === String(e.over!.id));
      const sortable = e.over.data.current?.sortable as { index?: number } | undefined;
      if (!isColumn && overColumnId(e.over) === d.columnId && sortable?.index != null) {
        insertAt = Math.max(0, Math.min(sortable.index, without.length));
      }
    }
    const beforeRank = insertAt > 0 ? without[insertAt - 1]!.columnRank : null;
    const afterRank = insertAt < without.length ? without[insertAt]!.columnRank : null;

    if (groupBy === 'lifecycle') {
      // Cross-column drag is a no-op — server doesn't change lifecycle via rank.
      if (d.columnId !== d.startColumnId) {
        setDrag(null);
        return;
      }
      commitDrop(d);
      rank.mutate(
        { itemId: dragged.id, ifMatch: dragged._etag, targetColumn: dragged.decision, beforeRank, afterRank },
        { onError: () => toast('Reordering failed — board refreshed') },
      );
      return;
    }

    const targetDecision = DECISION_COLUMNS.find((c) => c.id === d.columnId)?.decision ?? null;
    commitDrop(d);
    rank.mutate(
      { itemId: dragged.id, ifMatch: dragged._etag, targetColumn: targetDecision, beforeRank, afterRank },
      { onError: () => toast('Reordering failed — board refreshed') },
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={() => setDrag(null)}
    >
      <div className="flex gap-3 overflow-x-auto">
        {displayColumns.map((col) => (
          <KanbanColumn key={col.id} id={col.id} title={col.title} items={col.items} />
        ))}
      </div>
      <DragOverlay>{activeItem ? <ItemCardOverlay item={activeItem} /> : null}</DragOverlay>
    </DndContext>
  );
}
